/**
 * Adapts the Neo4j entity graph to the shape the Agent Network canvas already expects.
 *
 * The canvas (pages/agent-network/components/NetworkGraph.jsx) was built to draw agents,
 * tools, models, users, datasources and sub-agents — it ships an SVG icon for each. The
 * ops topology it is fed today only ever returns two node types (agent, framework) and
 * one relationship (uses_framework), so most of that renderer has never had data to draw.
 * The graph has all of it, so this maps one onto the other rather than adding a second
 * canvas next to the existing one.
 *
 * One mismatch is worth knowing about: the graph carries relationships the canvas has no
 * branch for (IMPACTS, TRIGGERED, MAPPED_TO, VIOLATED, ALERTED). They are passed through
 * lowercased and fall through to the renderer's default edge style, which is the honest
 * outcome: the edges are real and are drawn, they just have no bespoke styling yet.
 */

import { resolveAgentName } from '../../utils/agentName';

export function isReservedAgentNode(node) {
  return [node?.agent_id, node?.agent_identifier, node?.label, node?.name, node?.id, node?.node_id].some(
    (value) => typeof value === 'string' && value.trim().startsWith('_'),
  );
}

// Graph label -> the type strings the canvas branches on, and the allow-list of what may
// be drawn.
//
// Everything here gets a lane, because the view is handed layer definitions derived from
// the data (see pages/agent-network/utils/deriveLayerDefs) rather than matching against a
// fixed list. That is what lets the compliance chain -- Detection, Control, Framework,
// Policy, AlertType -- appear at all; against the hardcoded lanes those types had no y
// position and every node of them stacked on a single point.
//
// CandidatePerson is deliberately absent. It is an identity-resolution artefact, not
// something an agent network describes.
// This map is a FILTER as well as a translation: anything absent is dropped by the
// `.filter((n) => NODE_TYPE[n.type])` below, silently and with no error. So a label the
// graph starts emitting that is missing here does not render wrong — it disappears.
//
// graph-worker now writes ontology v1, which renamed several labels. Measured against the
// rebuilt graph, the pre-ontology map alone dropped 225 of 464 nodes — 48% of the page,
// with nothing in the console. The renamed labels below map onto the SAME canvas types
// they always used, so the page draws exactly as before and the ops drill-down
// (`/agent-network/nodes/{type}/{name}`, which validates against its own list of type
// names) keeps working unchanged.
const NODE_TYPE = {
  Agent: 'agent',
  SubAgent: 'subagent',
  Tool: 'tool',
  Model: 'model',
  User: 'user',
  Datasource: 'datasource',
  // Not 'framework' — see the note below the map.
  Framework: 'control_framework',
  Application: 'app',
  MCPServer: 'mcp',
  AgentFramework: 'framework',
  Topic: 'topic',
  Project: 'project',
  Detection: 'detection',
  Control: 'control',
  Policy: 'policy',
  AlertType: 'alerttype',

  // --- ontology v1 renames, mapped onto the existing canvas types ---------------
  Account: 'user',         // a per-surface identity is what `User` always was
  Datastore: 'datasource',
  Dataset: 'topic',        // Copilot Studio topics scope retrievable content

  // Resource, CloudAccount and Person are deliberately ABSENT.
  //
  // They are not renames — they are new planes (cloud, resolved identity) with no
  // pre-ontology equivalent, so there is no canvas lane, no icon, and no ops drill-down
  // that would accept them: clicking one would 400. Rendering them now would trade a
  // silent omission for a visibly broken click.
  //
  // They arrive with the agent-network rewrite, which is where the lanes and the
  // graph-backed detail panel land together. Until then this file's job is to keep the
  // page identical to what it was before the re-key.
};

// "framework" means two unrelated things across the two sources, and mapping both onto
// one canvas type would put ISO 42001 and langchain in the same lane behind the same
// icon:
//
//   ops / registry   framework = the SDK an agent is built with (langchain, crewai, adk)
//   graph            Framework = a governance standard (ISO 42001, SOC 2, EU AI Act)
//
// The canvas type `framework` keeps the SDK meaning, which is the one it was built for.
// Compliance frameworks get their own type. They cannot collide today because the source
// toggle picks one or the other, but the registry enrichment that would merge them is the
// obvious next step, and this is cheaper to separate now than to untangle later.
//
// A third sense of the same idea is already distinct and should stay that way: the
// graph's Agent.platform (custom, copilot_studio, gemini_enterprise) is the surface the
// telemetry arrived on, not what the agent was written with.

// Graph relationship -> the link types the renderer branches on. Relationships without a
// bespoke style fall through to its default edge, which is the honest outcome: the edge
// is real and is drawn, it just has no styling of its own yet.
const LINK_TYPE = {
  HOSTS_AGENT: 'hosts_agent',
  USES_MODEL: 'uses_model',
  USES_FRAMEWORK: 'uses_framework',
  CALLS_TOOL: 'has_tool',
  USED_TOOL: 'has_tool',
  HAS_SUBAGENT: 'calls_sub_agent',
  INVOKED: 'user_calls_agent',
  USED_APPLICATION: 'used_application',
  CONNECTS_TO_MCP: 'connects_to_mcp',
  ACCESSES_DATASOURCE: 'network_connection',
  ACCESSED_VIA: 'network_connection',
};

export function toTopology(graph) {
  const nodes = (graph?.nodes || [])
    .filter((n) => NODE_TYPE[n.type] && !(
      (n.type === 'Agent' || n.type === 'SubAgent') && isReservedAgentNode(n)
    ))
    .map((n) => ({
      id: n.id,
      // A custom Agent's graph label used to fall back to the @traceable span
      // name. Resolve it from stable identity fields instead. Other entity
      // labels are not agent names and remain untouched.
      label: n.type === 'Agent' && (
        n.platform === 'custom' || (Array.isArray(n.surfaces) && n.surfaces.includes('custom'))
      ) ? resolveAgentName(n) : n.label,
      type: NODE_TYPE[n.type],
      // Marks the node as graph-sourced. The detail sidebar uses this to decide whether
      // an ops lookup can succeed at all, rather than issuing one that cannot.
      entity_type: n.type,
      // Presentation views may normalize SubAgent to Agent, so retain a lowercase
      // source type for API routing and detail rendering.
      source_type: n.type ? String(n.type).toLowerCase() : undefined,
      platform: n.platform,
      // Full origin list, not just `platform`. Ontology keying merges one entity across
      // surfaces -- a person seen on Copilot and Gemini Enterprise is a single Account --
      // so the server projects `platform` as head(surfaces), whichever origin happened to
      // be written first. Filtering on that scalar drops every user whose first-seen
      // surface is not the selected one: on org 3 it left exactly one of eight Gemini
      // Enterprise users on the canvas. The array is what the server's own Cypher tests.
      surfaces: Array.isArray(n.surfaces) ? n.surfaces : (n.platform ? [n.platform] : []),
      // Agents live in two id-spaces: the graph keys them by slug, the ops registry by a
      // numeric id. Only the agents seen in trace enrichment carry both. Passing whatever
      // is present lets the sidebar resolve the ones that can be resolved.
      agent_id: n.agent_id || undefined,
      numeric_id: n.numeric_id || undefined,
      call_count: n.degree ?? 0,    // the canvas sizes/labels off this
      degree: n.degree ?? 0,
      last_seen: n.last_seen || null,
    }));

  // Edges to dropped nodes have to go too, or the layout tries to route a line to
  // something it never received.
  const kept = new Set(nodes.map((n) => n.id));
  const links = (graph?.edges || [])
    .filter((e) => kept.has(e.source) && kept.has(e.target))
    .map((e) => ({
      source: e.source,
      target: e.target,
      type: LINK_TYPE[e.type] || String(e.type || '').toLowerCase(),
      rel_type: e.type,             // the original relationship, for tooltips
      count: e.count ?? 1,
      last_seen: e.last_seen || e.lastSeen || null,
    }));

  return { has_data: nodes.length > 0, nodes, links };
}
